import React, { useEffect, useRef, useState } from 'react';

import { InfraPlan } from '../../models/plan/plan';
import { OgcParams } from '../../models/geo/ogcParams';
import { Layer } from '../../models/maps/layer';

import MapLayerControl from '../maps/MapLayerControl';
import MapSearchControl from '../maps/MapSearchControl';

import { ApiError } from '../../services/utils/apiError';

import { eventBus } from '../../services/event-emitter.service';
import { plannenService } from '../../services/plannen.service';
import { mapService } from '../../services/map.service';
import { helperService } from '../../services/helper.service';
import { planManagementService } from '../../services/plan-management.service';
import { assetsService } from '../../services/assets.service';
import { organisatieService } from '../../services/organisaties.service';
import { layerService } from '../../services/layer.service';

import { FeatureCollection } from 'geojson';
import * as atlas from 'azure-maps-control'; // Import the Azure Maps SDK
import 'azure-maps-control/dist/atlas.min.css';
import { authService } from '../../services/auth.service';
import { ListParams } from '../../models/listParams';


interface AzureMapProps {
  portfolioId?: string; // ID of the current portfolio
  portfolioActiveParticipant?: boolean; // Active participant in the portfolio
}

const AzureMap: React.FC<AzureMapProps> = ({
  portfolioId,
  portfolioActiveParticipant
}) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  // const mapInstance = useRef<atlas.Map | null>(null);
  const searchTextRef = useRef<string | null>(null);
  const filterRef = useRef<string | null>(null);
  const [currentZoomLevel, setCurrentZoomLevel] = useState<number | null>(null);

  // Use a Map to store data sources for each discipline
  const dataSourceMapRef = useRef<Map<string, atlas.source.DataSource>>(new Map());
  const popupRef = useRef<atlas.Popup | null>(null); // Ref for popup

  // State to hold layers
  const [activeLayers, setActiveLayers] = useState<Layer[]>([]);
  const [mapLoadError, setMapLoadError] = useState<boolean>(false);

  const activeLayersRef = useRef<Layer[]>([]);
  const mapErrorHandlerRef = useRef<(event: any) => void>();
  const mapClickHandlerRef = useRef<(e: any) => void>();

  const activeWmsLayersRef = useRef<Map<string, atlas.layer.TileLayer>>(new Map());
  const activeAssetLayersRef = useRef<Map<string, {
    dataSource: atlas.source.DataSource;
    layers: atlas.layer.Layer[];
    clickHandlers: Array<(e: any) => void>;
    moveHandler: () => void;
  }>>(new Map());

  const escapeHtml = (value: string): string => {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const isFilledPropertyValue = (value: unknown): boolean => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value as Record<string, unknown>).length > 0;
    }

    return true;
  };

  const formatPropertyLabel = (key: string): string => {
    const spaced = key
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();

    if (!spaced) {
      return key;
    }

    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const formatPropertyValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    return JSON.stringify(value);
  };

  const buildAssetPopupContent = (layerDisplayName: string, properties: Record<string, unknown>): string => {
    const rows = Object.entries(properties)
      .filter(([key, value]) => !key.toLowerCase().startsWith('infpl') && isFilledPropertyValue(value))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => {
        return `<label>${escapeHtml(formatPropertyLabel(key))}</label> ${escapeHtml(formatPropertyValue(value))} <br/>`;
      })
      .join('');

    return `
      <div class="popup-content popup-plan">
        <h5>${escapeHtml(layerDisplayName || 'Kaartlaag')}</h5>
        <div class="popup-data">
          ${rows || '<span>Geen eigenschappen gevonden</span>'}
        </div>
      </div>
    `;
  };

  const handleZoomInOnPlanBbox = (bbox: [number, number, number, number]) => {
    // console.log('Received zoomInOnPlanBbox event with bbox:', bbox);
    if (!bbox) {
      // Trigger with current map bounds if no bbox is provided
      gePlannenOnChangeMap();
      return;
    }

    if (!mapService.isValidBbox(bbox)) {
      console.warn('Ongeldige bbox ontvangen voor zoomInOnPlanBbox:', bbox);
      return;
    }
    
    // console.log('Zooming to bbox:', bbox);
    const bounds = mapService.convertBboxRDToLatLng(bbox, true) as number[];
    // console.log('Converted to LatLng:', bounds);

    const map = mapService.getMapInstance();
    if (map) {
      // drawBbox(map, bbox);
      map.setCamera({
        bounds: bounds,
        padding: 50, // Add padding for a better view
        duration: 1000, // Smooth animation
      });
    }
  };

  const setOgcFeatureFilterParams = (
    searchText: string, 
    filter: string
  ) => {
    searchTextRef.current = searchText;
    filterRef.current = filter;
  };  

  useEffect(() => {
    activeLayersRef.current = activeLayers;
  }, [activeLayers]);

  useEffect(() => {
    eventBus.on('zoomInOnPlanBbox', handleZoomInOnPlanBbox);
    eventBus.on('setOgcFilter', setOgcFeatureFilterParams);

    if (mapRef.current) {

      const map = initializeMap(mapRef.current);
      if (map) {
        const errorHandler = (event: any) => {
          const sourceId = event?.sourceId ?? event?.error?.sourceId ?? null;
          const layerId  = event?.layerId  ?? event?.error?.layerId  ?? null;
          const tileId   = event?.tileId   ?? event?.error?.tileId   ?? null;

          const details = {
            message: event?.message ?? event?.error?.message ?? 'Onbekende Azure Maps fout',
            source: event?.source ?? event?.error?.source ?? null,
            sourceId,
            layerId,
            tileId,
            style: map.getStyle()?.style,
          };

          console.error('Azure Maps runtime error', details, event);

          // Fatale fout: stijl kon niet geladen worden (geen source/layer/tile = init-niveau)
          if (!sourceId && !layerId && !tileId) {
            setMapLoadError(true);
          }
        };

        mapErrorHandlerRef.current = errorHandler;
        map.events.add('error', errorHandler);

        map.events.add('ready', async () => {
          await plannenService.getDisciplines();
          if (authService.isAdmin()) {
            await organisatieService.getAllOrganisatiesCached();
          }

          // Get both public WMS and asset layers and store in state.
          const [publicLayers, assetLayerResult] = await Promise.all([
            planManagementService.getActiveLayers(),
            assetsService.getAssetsLayers({
              _offset: 0,
              _limit: 500,
            } as ListParams),
          ]);

          const mappedAssetLayers: Layer[] = (assetLayerResult?.items ?? []).map(assetLayer => {
            const organizationId = assetLayer.sourceOrganizationId ?? '';
            const organizationName = organizationId
              ? organisatieService.getOrganisatieNaam(organizationId)
              : undefined;

            return {
              id: assetLayer.id ?? '',
              organizationId,
              url: '',
              sourceName: assetLayer.sourceName ?? organizationName ?? 'Overig',
              layerDisplayName: assetLayer.layerDisplayName ?? '',
              description: assetLayer.description,
              assetCount: assetLayer.assetCount,
              type: 'geopackage' as const,
              selected: false,
            } as Layer;
          }).filter(layer => !!layer.id && !!layer.layerDisplayName);

          const mappedPublicLayers = publicLayers.map(layer => ({
            ...layer,
            selected: layer.selected ?? false,
          }));

          setActiveLayers([...mappedPublicLayers, ...mappedAssetLayers]);

          initializeLayerAndEvents(map);
          // Listen for bounding box changes (on zoom, pan, etc.)
          map.events.add('moveend', gePlannenOnChangeMap);
          gePlannenOnChangeMap(); // Trigger once
        });

        const handler = (e: any) => {
          const currentLayers = activeLayersRef.current.filter(l => l.selected);
          handleMapClick(e, map, currentLayers);
        };
      
        mapClickHandlerRef.current = handler;
      
        map.events.add("click", handler);
      
        mapService.setMapInstance(map);
      }

      // map.events.add('load', async () => {
      //   // Update the style of mouse cursor to a pointer
      //   map.getCanvasContainer().style.cursor = 'pointer';
      // });

       // // Upon a mouse click, open a popup at the selected location and render in the popup the address of the selected location
      // map.events.add('click', async (e) => {
      //   const position = [e.position[0], e.position[1]];
      //   //console.log(position);
      // });
    }

    // Cleanup map instance on unmount
    return () => {
      eventBus.off('zoomInOnPlanBbox', handleZoomInOnPlanBbox); // Cleanup to prevent memory leaks

      const map = mapService.getMapInstance();
      if (map) {
        
        if (mapClickHandlerRef.current) {
          map.events.remove("click", mapClickHandlerRef.current);
        }

        if (mapErrorHandlerRef.current) {
          map.events.remove('error', mapErrorHandlerRef.current);
        }

        map.events.remove('moveend', gePlannenOnChangeMap);
        map.dispose(); // Ensure the map is destroyed properly
        mapService.setMapInstance(null);  // Set map instance to null after cleanup
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeMap = (mapContainer: HTMLDivElement) => {
    const anyCoordinates = [4.5081147, 52.10353492]; // This is Leiden // 4.308659728050088,52.10670440273557,4.7172424811051314,52.21878174517474

    let cachedToken: string | null = null;
    let tokenExpireTime: number | null = null;

    // Initialize the map
    const map = new atlas.Map(mapContainer, {
      center: anyCoordinates, // [-122.33, 47.6], // Default center [Longitude, Latitude]
      zoom: 11, // 15, // 9,
      language: 'nl-NL', // 'en-US',
      style: 'grayscale_light',
      // scrollZoomInteraction: false,
      // view: 'Auto',
      authOptions: {
        authType: atlas.AuthenticationType.sas,
        getToken: async (resolve, reject) => {
          try {
  
            const now = new Date().getTime();
  
            // Check if cached token is still valid (refresh 2 minutes - 120000 before expiry)
            if (cachedToken && tokenExpireTime && now < tokenExpireTime - 60000) { // 60 sec
              resolve(cachedToken);
              return;
            }
  
            const response = await planManagementService.getAzureMapsToken();
  
            cachedToken = response.token;
            tokenExpireTime = new Date(response.expiresAtUtc).getTime();
  
            resolve(cachedToken);
  
          } 
          catch (error) {
            console.error("Azure Maps token ophalen mislukt:", error);
            reject(error);
          }
        }
      },
    });

    // Add Fullscreen control to the map
    map.controls.add(new atlas.control.FullscreenControl(), {
      position: atlas.ControlPosition.BottomLeft
    });

    // Add Zoom control to the map
    map.controls.add(new atlas.control.ZoomControl(), {
      position: atlas.ControlPosition.BottomLeft,
    });

    // Add pitch control and add it to the map.
    map.controls.add(new atlas.control.PitchControl(), {
      position: atlas.ControlPosition.BottomLeft,
    });

    // Add compass control and add it to the map.
    map.controls.add(new atlas.control.CompassControl(), {
      position: atlas.ControlPosition.BottomLeft,
    });

    // Add style control and add it to the map.
    map.controls.add(new atlas.control.StyleControl({
        mapStyles: ['road', 'grayscale_light', 'grayscale_dark', 'night', 'road_shaded_relief', 'satellite', 'satellite_road_labels'],
        layout: 'icons' // 'list'
      }),
      {
        position: atlas.ControlPosition.BottomLeft,
      }
    );

    map.controls.add(
      new atlas.control.ScaleControl({
        maxWidth: 100, // breedte van balk in pixels
        units: "metric", // "imperial" kan ook
        showLabel: true  
      }),
      {
        position: atlas.ControlPosition.BottomRight
      }
    );

    return map;
  };

  const initializeLayerAndEvents = (map: atlas.Map | null) => {
    if (map) {
      const disciplines = plannenService.disciplines; // : any[] = []; //
      const dataSourcePolygons = new atlas.source.DataSource(undefined, { buffer: 0 });
      dataSourceMapRef.current.set('Default', dataSourcePolygons);
      map.sources.add(dataSourcePolygons);

      if (disciplines) {
        disciplines.forEach(discipline => {
          // // Create a new DataSource for each discipline
          // const dataSourcePolygons = new atlas.source.DataSource();
          // dataSourceMapRef.current.set(discipline.name, dataSourcePolygons);
          // map.sources.add(dataSourcePolygons);

          const polygonLayerDiscipline = new atlas.layer.PolygonLayer(dataSourcePolygons, undefined, {
            // filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],	// Only render Polygon or MultiPolygon in this layer.
            fillColor: discipline.colorRgb,
            fillOpacity: 0.2,
            // strokeColor: 'rgb(0, 0, 0)',// 'rgb(61, 108, 180)',
            // strokeWidth: 2,
            // strokeOpacity: 1,
            filter: ['==', ['get', 'Discipline'], discipline.name]
          });

          const lineLayerDiscipline = new atlas.layer.LineLayer(dataSourcePolygons, undefined, {
            strokeColor: discipline.colorRgb,
            strokeWidth: 1,
            filter: ['==', ['get', 'Discipline'], discipline.name]
          });

          map.layers.add([polygonLayerDiscipline, lineLayerDiscipline], 'labels');

          // Initialize the popup
          if (!popupRef.current) {
            popupRef.current = new atlas.Popup({
              pixelOffset: [0, -20], // Offset for popup
              closeButton: true,
            });
          }

          // Add mouse event listeners to display the popup on hover
          map.events.add('click', polygonLayerDiscipline, (e) => {
            if (e.shapes && e.shapes.length > 0) {
              const shape = e.shapes[0];
              let item;
              if (shape) {
                let properties = null;

                // Check if shape is an atlas.Shape or a raw GeoJSON feature
                if (shape instanceof atlas.Shape) {
                  properties = shape.getProperties(); // For atlas.Shape
                } 
                else if (shape instanceof atlas.data.Feature) {
                  properties = shape.properties; // For GeoJSON Feature
                }

                if (properties) {
                  item = properties.item as InfraPlan;
                  
                  let addPlanToPortfolioLink = '';

                  if (portfolioId && portfolioActiveParticipant && authService.canCrud()) {
                    addPlanToPortfolioLink = `
                        <a href="#" id="link-add-plan" data-id="${item.infplId}" 
                          class="lnk lnk-icon-button tooltip-action" 
                          title="Plan toevoegen aan dit afstemmingsdossier"
                        >
                          <i class="material-icons">add</i>
                          <span>Plan toevoegen</span>
                        </a>
                      `;
                  }

                  const popupContent = `
                        <div class="popup-content popup-plan">
                          <h5>${item.planNaam || 'Geen'}</h5>
                          <span 
                            class="category-icon category-icon-small ${helperService.toCamelCase(item.discipline)}-icon-white tooltip-action" 
                            title="${item.discipline}">
                          </span>
                          <div class="popup-data">
                            <label>PlanID</label> ${item.planId || 'Geen'} <br/>
                            <label>Organisatie</label> ${item.infplOrganizationName || 'Geen'} <br/>
                            <label>Status</label> ${item.status || 'Geen'} <br/>
                            <label>Startdatum</label> ${item.startDatum ? new Date(item.startDatum).toLocaleDateString('nl-NL') : '-'} <br/>
                            <label>Einddatum</label> ${item.eindDatum ? new Date(item.eindDatum).toLocaleDateString('nl-NL') : '-'} <br/>
                          </div>
                          <div class="popup-button-group">
                            ${addPlanToPortfolioLink}
                            <a href="/plannen/${item.infplId}" class="lnk lnk-icon tooltip-action" title="Open het plan">
                              <span class="material-icons">arrow_forward</span>
                            </a>
                          </div>
                        </div>
                      `;

                  // Set the popup content
                  if (popupRef.current) {
                    popupRef.current.setOptions({
                      content: popupContent,
                      position: e.position
                    });
      
                    // Open the popup
                    popupRef.current.open(map);

                    // Add event listener to trigger eventBus event
                    setTimeout(() => {
                      const addPlanLink = document.getElementById('link-add-plan');
                      if (addPlanLink) {
                        addPlanLink.addEventListener('click', (event) => {
                          event.preventDefault(); // Prevent default navigation behavior
                          const planId = addPlanLink.getAttribute('data-id');
                          if (planId) {
                            eventBus.emit('addPlanToPortfolio', planId, portfolioId);
                          }
                        });
                      }
                    }, 100);
                  }
                }
              }
            }
          });

          // // Hide the popup when clicking anywhere else on the map
          // map.events.add('click', (e) => {
          //   if (popupRef.current) {
          //     popupRef.current.close();
          //   }
          // });

          // // Add mouse event listeners to display the popup on hover
          // map.events.add('mousemove', polygonLayerDiscipline, (e) => {
          //   if (e.shapes && e.shapes.length > 0) {
          //     const shape = e.shapes[0];
          //     let item;
          //     if (shape) {
          //       let properties = null;

          //       // Check if shape is an atlas.Shape or a raw GeoJSON feature
          //       if (shape instanceof atlas.Shape) {
          //         properties = shape.getProperties(); // For atlas.Shape
          //       } 
          //       else if (shape instanceof atlas.data.Feature) {
          //         properties = shape.properties; // For GeoJSON Feature
          //       }

          //       if (properties) {
          //         item = properties.item as InfraPlan;

          //         // Create the content for the popup based on the properties of the polygon
          //         // <p><strong>Published:</strong> ${item.infplPublisherName || 'Geen'}</p>
          //         // <p><strong>Omschrijving:</strong> ${item.omschrijving || 'Geen'}</p>

          //         const popupContent = `
          //           <div class="popup-content">
          //             <h5>${item.planNaam || 'Geen'}</h5>
          //             <p><strong>OrganisatieId:</strong> ${item.organisationId || 'Geen'}</p>
          //             <p><strong>Status:</strong> ${item.status || 'Geen'}</p>
          //             <p><strong>Discipline:</strong> ${item.discipline || 'Geen'}</p>
          //           </div>`;

          //         // Set the popup content
          //         if (popupRef.current) {
          //           popupRef.current.setOptions({
          //             content: popupContent,
          //             position: e.position
          //           });
      
          //           // Open the popup
          //           popupRef.current.open(map);
          //         }
          //       }
          //     }
          //   }
          // });

          // // Hide the popup when the mouse leaves the polygon
          // map.events.add('mouseleave', polygonLayerDiscipline, () => {
          //   if (popupRef.current) {
          //     popupRef.current.close();
          //   }
          // });

          // // Close the popup when the mouse moves on the map.
          // this.map.events.add('mouseleave', polygonLayerDiscipline, this.closePolygonPopup);
          // //Show the popup
          // this.map.events.add('mousemove', polygonLayerDiscipline, this.showPolygonPopup);
          // this.map.events.add('touchstart', polygonLayerDiscipline, this.showPolygonPopup);
        });

        // const fallbackFilterZoMoetenWerken: any = [
        //   'any',
        //   // Case 1: Discipline ontbreekt
        //   ['!', ['has', 'Discipline']],
        //   // Case 2: Discipline zit NIET in de bekende lijst
        //   [
        //     'all',
        //     ...knownDisciplines.map(d => ['!=', ['get', 'Discipline'], d])
        //   ]
        // ];
        
        // 1) Verzamel bekende disciplines uit dezelfde bron als je specifieke lagen
        const knownDisciplines: string[] = (disciplines ?? []).map(d => String(d.name).trim()); // voorkom spaties/undefined

        // 2) Bouw het fallback filter
        const fallbackFilter: any = buildFallbackFilter('Discipline', knownDisciplines);

        const fallbackLayerPolygon = new atlas.layer.PolygonLayer(dataSourcePolygons, undefined, {
          fillColor: 'rgb(90, 90, 90)',
          fillOpacity: 0.2,
          filter: fallbackFilter
        });

        const fallbackLayerLine = new atlas.layer.LineLayer(dataSourcePolygons, undefined, {
          strokeColor: 'rgb(90, 90, 90)',
          strokeWidth: 1,
          filter: fallbackFilter
        });

        // console.log('fallback layer added')
        map.layers.add([fallbackLayerPolygon, fallbackLayerLine], 'labels');

        // map.events.add('click', fallbackLayerPolygon, e => {
        //   const p = (e.shapes?.[0] as atlas.Shape)?.getProperties?.();
        //   console.log('Discipline =', p?.Discipline);
        // });
      }
      else {
        console.log('Geen disciplines: ', disciplines);

        // const dataSourcePolygons = new atlas.source.DataSource();
        // dataSourceMapRef.current.set('Default', dataSourcePolygons);
        // map.sources.add(dataSourcePolygons);

        // Add a layer for polygons
        const polygonLayer = new atlas.layer.PolygonLayer(dataSourcePolygons, undefined, {
          fillColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent BLACK
          strokeColor: 'rgba(0, 0, 0, 0.9)', //  'BLACK',
          strokeWidth: 2,
          // filter: ['==', ['get', 'Discipline'], 'Water']
        });

        const lineLayer = new atlas.layer.LineLayer(dataSourcePolygons, undefined, {
          strokeColor: 'rgb(0, 0, 0)', // planItem.colorRgb, // BLACK
          strokeWidth: 1
        });

        map.layers.add([polygonLayer, lineLayer], 'labels');
      }
    }
  };

  /**
   * Maak een filter dat true is wanneer het veld exact gelijk is aan één van de waarden.
   * Equivalent van "IN (…)" maar zonder variadische 'in'.
   */
  function equalsAny(field: string, values: string[]): any {
    return ['any', ...values.map(v => ['==', ['get', field], v])];
  }

  /**
   * Fallback = (géén Discipline) OF (Discipline NIET in bekende lijst)
   */
  function buildFallbackFilter(field: string, known: string[]): any {
    return [
      'any',
      ['!', ['has', field]],
      ['!', equalsAny(field, known)]
    ];
  }

  // Function to update polygons based on the current bbox
  const gePlannenOnChangeMap = async () => {
    const map = mapService.getMapInstance();
    if (map) { // && dataSourceRef.current
      const zoomLevel = map.getCamera().zoom;

      if (zoomLevel) {
        setCurrentZoomLevel(zoomLevel);
      }

      // console.log('Zoom:', zoomLevel);
      if (zoomLevel && zoomLevel >= 10) {
        const bbox = map.getCamera().bounds; // Get current bounding box (bbox)
        if (bbox) {
          // const [west, south, east, north] = bbox;
          // Draw bbox on the map for testing
          // drawBbox(map, [west, south, east, north]);

          // console.log('Current BBox:', bbox);
          // console.log('zoom:', zoomLevel);

          // Convert bbox coordinates from EPSG:4326 to EPSG:28992
          const bboxString = mapService.convertBboxLatLngToRD(bbox) as string;
          // console.log(`bboxString (RD): ${bboxString}`);

          // const [west, south] = proj4("EPSG:4326", "EPSG:28992", [bbox[0], bbox[1]]);
          // const [east, north] = proj4("EPSG:4326", "EPSG:28992", [bbox[2], bbox[3]]);

          const ogcParams = new OgcParams();
          ogcParams.offset = 0;
          ogcParams.limit = 1000;
          ogcParams.bbox = bboxString;
          if (searchTextRef.current) {
            ogcParams.searchText = searchTextRef.current;
          }
          if (filterRef.current) {
            ogcParams.filter = filterRef.current;
          }

          ogcParams.properties = 'infplId,planId,planNaam,planStartDatum,planEindDatum,planStatus,discipline,infplOrganizationName' // 'planNaam,organisationName,status,discipline,infplId';
          
          // Fetch polygons based on bbox. For now, we will create a static polygon inside the bbox
          // Get geoJSON data from the API based on the bbox
          const data = await plannenService.getPlannenOgcFeatures(ogcParams);

          // If you receive GeoJSON, add it to the map
          if (data && data.features) {
            // if (data.features.length > 0) {
            //   //console.log('items found:', data);
            // }

            clearDataSource();
            createPolygonsOnMap(data);
          }
        }
      }
      else {
        // console.log(`Zoomlevel ${zoomLevel} is to low to load plans on map. Minimum zoom level is 11.`);
        clearDataSource();

        // if (this.doesExist(this.renderedPolygonIds)) {
        //   this.renderedPolygonIds.clear();
        // }
        // if (this.doesExist(this.shapesForBounds)) {
        //   this.shapesForBounds.clear();
        // }
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const drawBbox = (map: atlas.Map, bbox: [number, number, number, number], color: string = '#ff0000') => {
    if (map) {
      const [west, south, east, north] = bbox;

      // console.log('bbox: ' + west + ', ' + south + ', ' + east + ', ' + north);

      // Een GeoJSON Polygon aanmaken voor het kader
      const bboxPolygon = new atlas.data.Feature(
        new atlas.data.Polygon([[
          [west, south],
          [east, south],
          [east, north],
          [west, north],
          [west, south] // terug naar startpunt!
        ]]),
        { name: 'Bounding Box' }
      );
    
      // DataSource aanmaken (één keer doen, buiten bijv. opslaan als ref)
      const bboxDataSource = new atlas.source.DataSource(); // 'bbox-source'
      map.sources.add(bboxDataSource);
    
      // Polygon toevoegen aan datasource
      bboxDataSource.add(bboxPolygon);
    
      // Laag toevoegen om het kader zichtbaar te maken
      const bboxLayer = new atlas.layer.LineLayer(bboxDataSource, 'bbox-layer', {
        strokeColor: color,
        strokeWidth: 3,
        lineDashArray: [2, 2], // stippellijn
      });
      map.layers.add(bboxLayer);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const drawPolygon = (coordinates: number[][]) => {
    const map = mapService.getMapInstance();
    if (!map) {
      console.warn("Azure Maps instance not available.");
      return;
    }

    // Convert RD coordinates to WGS84
    let transformedCoordinates = mapService.convertRDToWGS84(coordinates);
  
    // Ensure the polygon is closed
    if (
      transformedCoordinates.length > 0 &&
      (transformedCoordinates[0][0] !== transformedCoordinates[transformedCoordinates.length - 1][0] ||
        transformedCoordinates[0][1] !== transformedCoordinates[transformedCoordinates.length - 1][1])
    ) {
      transformedCoordinates.push([...transformedCoordinates[0]]);
    }

    // Compute centroid (middle point) of the polygon
    const centroid = mapService.calculateCentroid(transformedCoordinates);
  
    // Create a polygon shape
    const polygon = new atlas.data.Polygon([coordinates]);
  
    // Create a feature shape with properties
    const polygonFeature = new atlas.Shape(
      new atlas.data.Feature(polygon, {
        color: "rgba(0, 0, 0, 0.5)", // BLACK fill with transparency
      })
    );
  
    // Add to the data source
    const dataSource = new atlas.source.DataSource();
    map.sources.add(dataSource);
    dataSource.add(polygonFeature);
  
    // Create a polygon layer
    const polygonLayer = new atlas.layer.PolygonLayer(dataSource, undefined, {
      fillColor: "rgba(0, 0, 0, 0.3)", // BLACK fill with transparency
      strokeColor: "blue",
      strokeWidth: 2,
    });
  
    map.layers.add(polygonLayer);

    // Add marker (symbol layer) at the centroid
    dataSource.add(new atlas.data.Point(centroid));

    const symbolLayer = new atlas.layer.SymbolLayer(dataSource, undefined, {
      iconOptions: {
        image: "marker-blue",
        allowOverlap: true,
        size: 0.8,
      },
    });
  
    map.layers.add(symbolLayer);
  };

  // /**
  //  * Converts EPSG:28992 Polygon/MultiPolygon coordinates to EPSG:4326 for Azure Maps
  //  */
  // const convertRDToWGS84 = (coordinates) => {
  //   return coordinates.map((polygonOrRing) =>
  //     polygonOrRing.map((ringOrPoint) =>
  //       Array.isArray(ringOrPoint[0]) // Detect if it's a polygon ring or a single point
  //         ? ringOrPoint.map(([x, y]) => proj4("EPSG:28992", "EPSG:4326", [x, y])) // Convert rings
  //         : proj4("EPSG:28992", "EPSG:4326", ringOrPoint) // Convert single coordinate pair
  //     )
  //   );
  // };
  

  // EPSG:28992 version implementation
  const createPolygonsOnMap = (geoJsonData: FeatureCollection) => {
    const map = mapService.getMapInstance();
    if (!map) return;

    if (geoJsonData && geoJsonData.features) {
      const dataSourcePolygons = dataSourceMapRef.current.get("Default");
  
      geoJsonData.features.forEach((feature) => {
        if (!feature.geometry) return;

        let transformedCoordinates;

        switch (feature.geometry.type) {
          case "Polygon":
            transformedCoordinates = mapService.convertRDToWGS84([feature.geometry.coordinates])[0]; // Wrap for compatibility
            break;
            
          case "MultiPolygon":
            transformedCoordinates = mapService.convertRDToWGS84(feature.geometry.coordinates);
            break;

          case "LineString":
            transformedCoordinates = mapService.convertRDToWGS84(feature.geometry.coordinates);
            break;

          default:
            console.warn("Unsupported geometry type:", feature.geometry.type);
            return;
        }

        // // Compute centroid (middle point) of the polygon
        // const centroid = calculateCentroid(transformedCoordinates);

        // Create item with properties
        const item = new InfraPlan();
        item.infplId = feature.id?.toString();
        item.planId = feature.properties?.planId;
        item.planNaam = feature.properties?.planNaam;
        item.status = feature.properties?.planStatus;
        item.infplPublisher = feature.properties?.infplPublisher;
        item.infplPublisherName = feature.properties?.infplPublisherName;
        item.omschrijving = feature.properties?.omschrijving; 
        item.discipline = feature.properties?.discipline;
        item.infplOrganizationId = feature.properties?.infplOrganizationId;
        item.infplOrganizationName = feature.properties?.infplOrganizationName;
        item.startDatum = feature.properties?.planStartDatum;
        item.eindDatum = feature.properties?.planEindDatum;

        item.bboxRD = feature.bbox?.toString() ?? "";
        item.bboxLatlng = mapService.convertBboxRDToLatLng(feature.bbox as number[], true).toString();

        const discipline = plannenService.getDisciplineByName(feature.properties?.discipline);
        if (discipline) {
          item.discipline = discipline.name;
          item.colorRgb = discipline.colorRgb;
          item.colorRgba = discipline.colorRgba;
        }
        else {
          item.colorRgb = "rgb(236, 0, 83)";
          item.colorRgba = "rgba(236, 0, 83, 0.1)";
        }

        let shape;

        switch (feature.geometry.type) {
          case "Polygon":
            shape = new atlas.data.Polygon(transformedCoordinates);
            break;
            
          case "MultiPolygon":
            shape = new atlas.data.MultiPolygon(transformedCoordinates);
            break;

          case "LineString":
            shape = new atlas.data.LineString(transformedCoordinates);
            break;
        }

        if (shape) {
          const shapePolygonFeature = new atlas.Shape(
            new atlas.data.Feature(shape, {
              item,
              Discipline: item.discipline,
              rgbaColor: item.colorRgba,
            })
          );

          if (dataSourcePolygons) {
            dataSourcePolygons.add(shapePolygonFeature);
          }

          // // REFACTOR: Waar is deze voor?
          // // Extract the outer ring (first array) of the first polygon for a border
          // const outerRing = transformedCoordinates[0][0]; 
          // const shapeLineString = new atlas.Shape(
          //   new atlas.data.Feature(new atlas.data.LineString(outerRing))
          // );

          // if (dataSourcePolygons) {
          //   dataSourcePolygons.add(shapeLineString);
          // }
        }

      });
    }
  };


  // WGS84 version implentation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const createPolygonsOnMapWithLatLong = (geoJsonData: FeatureCollection) => {
    const map = mapService.getMapInstance();
    if (!map) return;

    // const newPolygonIds = new Set<string>();
    // let count = 0;
    if (geoJsonData && geoJsonData.features) {
      const dataSourcePolygons = dataSourceMapRef.current.get('Default');
      geoJsonData.features.forEach(feature => {
        if (feature.geometry && feature.geometry) {

          if (feature.geometry.type === 'Polygon') {
            // const polygonGeometry = geometry as Polygon;
            // console.log('Polygon coordinates:', polygonGeometry.coordinates);

            const item = new InfraPlan();
            // item.id = feature.id;
            // item.infplId = feature.id;
            item.planId = feature.properties?.planId;
            item.planNaam = feature.properties?.planNaam;
            item.status = 'Nog niet in de OGC';
            item.infplPublisher = feature.properties?.infplPublisher;
            item.infplPublisherName = feature.properties?.infplPublisherName;
            item.omschrijving = feature.properties?.omschrijving; // 'Omschrijving';
            item.discipline = 'Water'; // (feature.properties) ? feature.properties.discipline : 'Onbekend';
            
            const discipline = plannenService.getDisciplineByName(feature.properties?.discipline as string);
            if (discipline) {
              item.discipline = discipline.name; // Match niet gevonden dan indelen als onbekend
              item.colorRgb = discipline.colorRgb;
              item.colorRgba = discipline.colorRgba;
            }
            else {
              item.colorRgb = 'rgb(236, 0, 83)'; // this.helper.extendedRgbColors[index];
              item.colorRgba = 'rgba(236, 0, 83, 0.1)'; // this.helper.extendedRgbaColors[index];
            }

            // Create the polygon shape with the specified color
            const polygon = new atlas.data.Polygon(feature.geometry.coordinates);
            if (polygon) {
              // const polygonShape = new atlas.Shape(polygon, undefined, { item });

              const shapePolygonFeature = new atlas.Shape(new atlas.data.Feature(polygon, { item,
                Discipline: item.discipline,
                rgbaColor: item.colorRgba // Assumes rgbaColor is provided in OGC properties
              }));
              
              if (dataSourcePolygons) {
                dataSourcePolygons.add(shapePolygonFeature);
              }
              // addPolygonToDataSources(shapePolygonFeature, item.discipline);
              
              // Extract the outer ring (first array) of the Polygon
              const polygonCoordinates = polygon.coordinates as [number, number][][];
              const outerRing = polygonCoordinates[0]; // The first element is the outer boundary
              const shapeLineString = new atlas.Shape(new atlas.data.Feature(
                // new atlas.data.LineString(feature.geometry.coordinates)
                new atlas.data.LineString(outerRing)
              ));
              if (dataSourcePolygons) {
                dataSourcePolygons.add(shapeLineString);
              }
              // addPolygonToDataSources(shapeLineString, item.discipline);
            }
          }
        }
      });
    }
  };

  const clearDataSource = () => {
    const dataSourcePolygons = dataSourceMapRef.current.get('Default');
    if (dataSourcePolygons) {
      dataSourcePolygons.clear();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clearDataSources = (map: atlas.Map | null) => {
    if (!map) return;

    // Loop through each data source
    dataSourceMapRef.current.forEach((dataSource, discipline) => {
      // Find layers that use this data source
      const layersToRemove = map.layers.getLayers().filter(layer => {
        // Check if the layer's source matches this dataSource
        const source = (layer as any).getOptions().source;
        return source === dataSource;
      });

      // Remove each layer that uses the data source
      layersToRemove.forEach(layer => {
        map.layers.remove(layer);  // Remove the layer from the map
      });

      // Clear the data source
      dataSource.clear();

      // Remove the data source from the map
      map.sources.remove(dataSource);
    });

    // Optionally clear the reference to all data sources in the map
    dataSourceMapRef.current.clear();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addPolygonToDataSources = (shape: atlas.Shape, disciplineNaam: string | null) => {
    dataSourceMapRef.current.forEach((dataSource, discipline) => {
      // Find the corresponding data source for the discipline
      const disciplineDataSource = dataSourceMapRef.current.get((disciplineNaam) ? disciplineNaam : 'Default');
      if (disciplineDataSource) {
        // Add the shape to the correct data source
        disciplineDataSource.add(shape);
      }
      else {
        console.error(`No data source found for discipline: ${disciplineNaam}`);
      }
    });
  };



  // useEffect(() => {
  //   if (!mapRef.current) return;

  //   const map = initializeMap(mapRef.current);
  //   if (map) {
  //     const clickHandler = (e: any) => {
  //       const currentLayers = activeLayers.filter(l => l.selected);
    
  //       handleMapClick(e, map, currentLayers);
  //     };
    
  //     map.events.add("click", clickHandler);
    
  //     return () => {
  //       map.events.remove("click", clickHandler);
  //     };
  //   }
  // }, [activeLayers]);

  const handleMapClick = async (
    e: any,
    map: any,
    currentLayers: Layer[]
  ) => {
    // Alleen als 1 layer actief
    if (currentLayers.length !== 1) {
      console.warn("Meerdere of geen actieve layers");
      return;
    }
  
    const layer = currentLayers[0];

    // Asset layers are rendered from OGC features and do not support WMS GetFeatureInfo.
    if (layer.type !== 'wms' || !layer.url) {
      return;
    }
  
    try {
      const { base, params } = layerService.parseWmsBaseUrl(layer.url);
  
      const layersParam = params.get("layers")?.split(",") ?? [];
      const queryLayer = layersParam[0];
      
      // Check GetFeatureInfo support
      const hasFeatureInfo = await layerService.checkGetFeatureInfoSupport(base, queryLayer);
  
      if (!hasFeatureInfo) {
        return;
      }
  
      // Build request
      const url = layerService.buildGetFeatureInfoUrl({
        base,
        params,
        map,
        pixel: e.position
      });
  
      console.log("GetFeatureInfo URL:", url);

      const result = await fetch(url);
      const text = await result.text();

      let data;
      try {
        // data = await res.json();
        data = JSON.parse(text);
      } 
      catch {
        // data = await res.text();
        data = text;
      }
  
      console.log("FeatureInfo result:", data);
  
    } catch (err) {
      console.error("GetFeatureInfo error:", err);
    }
  };

  const onToggleActiveLayerGroup = (groupLayers: Layer[], checked: boolean) => {
    groupLayers.forEach(layer => {
      if ((layer.selected ?? false) !== checked) {
        onToggleActiveLayer(layer);
      }
    });
  };

  const onToggleActiveLayer = async (layer: Layer) => {
    if (layer.type === 'geopackage') {
      await onToggleAssetLayer(layer);
      return;
    }

    await onToggleWmsLayer(layer);
  };

  const onToggleWmsLayer = async (layer: Layer) => {
    const map = mapService.getMapInstance();
    if (!map) return;
  
    const existingLayer = activeWmsLayersRef.current.get(layer.id);
  
    if (existingLayer) {
      map.layers.remove(existingLayer);
      activeWmsLayersRef.current.delete(layer.id);
  
      setActiveLayers(prev =>
        prev.map(l =>
          l.id === layer.id ? { ...l, selected: false } : l
        )
      );
  
      return;
    }
  
    const wmsUrl = `${layer.url}&bbox={bbox-epsg-3857}`;
  
    const tileLayer = new atlas.layer.TileLayer({
      tileUrl: wmsUrl,
      tileSize: 256,
      opacity: 0.9
    });
  
    map.layers.add(tileLayer, "labels");
  
    activeWmsLayersRef.current.set(layer.id, tileLayer);
  
    setActiveLayers(prev =>
      prev.map(l =>
        l.id === layer.id ? { ...l, selected: true } : l
      )
    );
  };

  const convertGeometryCoordinatesToWgs84 = (coordinates: any): any => {
    if (!Array.isArray(coordinates)) {
      return coordinates;
    }

    if (
      coordinates.length >= 2
      && typeof coordinates[0] === 'number'
      && typeof coordinates[1] === 'number'
    ) {
      return mapService.convertRDToWGS84([coordinates[0], coordinates[1]]);
    }

    return coordinates.map(convertGeometryCoordinatesToWgs84);
  };

  const refreshAssetLayerFeatures = async (layer: Layer, dataSource: atlas.source.DataSource, map: atlas.Map) => {
    const bounds = map.getCamera().bounds;
    const ogcParams = new OgcParams();
    ogcParams.limit = 1000;
    if (Array.isArray(bounds) && bounds.length === 4) {
      ogcParams.bbox = mapService.convertBboxLatLngToRD(bounds) as string;
    }

    let featureCollection: FeatureCollection;
    try {
      featureCollection = await assetsService.getAssetLayerOgcFeatures(layer.id, ogcParams);
    }
    catch (error) {
      if (error instanceof ApiError && error.isNotFound()) return;
      console.error('Fout bij ophalen van geopackage layer OGC features:', error);
      return;
    }

    const crsName = (featureCollection as any)?.crs?.properties?.name as string | undefined;
    const shouldConvertFromRd = !!crsName && crsName.toUpperCase().includes('28992');

    // const dataSource = new atlas.source.DataSource(`asset-layer-${layer.id}`, { buffer: 0 });
    // map.sources.add(dataSource);
    dataSource.clear();

    (featureCollection?.features ?? []).forEach((feature: any) => {
      const geometry = feature?.geometry;
      if (!geometry) return;

      const geometryCoordinates = shouldConvertFromRd
        ? convertGeometryCoordinatesToWgs84(geometry.coordinates)
        : geometry.coordinates;

      let atlasGeometry: atlas.data.Geometry | null = null;
      switch (geometry.type) {
        case 'Polygon': atlasGeometry = new atlas.data.Polygon(geometryCoordinates); break;
        case 'MultiPolygon': atlasGeometry = new atlas.data.MultiPolygon(geometryCoordinates); break;
        case 'LineString': atlasGeometry = new atlas.data.LineString(geometryCoordinates); break;
        case 'MultiLineString': atlasGeometry = new atlas.data.MultiLineString(geometryCoordinates); break;
        case 'Point': atlasGeometry = new atlas.data.Point(geometryCoordinates); break;
        case 'MultiPoint': atlasGeometry = new atlas.data.MultiPoint(geometryCoordinates); break;
      }

      if (!atlasGeometry) return;

      dataSource.add(new atlas.Shape(
        new atlas.data.Feature(atlasGeometry, {
          ...feature.properties,
          layerName: layer.layerDisplayName,
        })
      ));
    });
  };

  const onToggleAssetLayer = async (layer: Layer) => {
    const map = mapService.getMapInstance();
    if (!map || !layer.id) return;

    const existingAssetLayer = activeAssetLayersRef.current.get(layer.id);

    if (existingAssetLayer) {
      map.events.remove('moveend', existingAssetLayer.moveHandler);

      existingAssetLayer.layers.forEach((mapLayer, index) => {
        const clickHandler = existingAssetLayer.clickHandlers[index];
        if (clickHandler) {
          map.events.remove('click', mapLayer, clickHandler);
        }
      });

      existingAssetLayer.layers.forEach(mapLayer => {
        map.layers.remove(mapLayer);
      });

      map.sources.remove(existingAssetLayer.dataSource);
      activeAssetLayersRef.current.delete(layer.id);

      setActiveLayers(prev =>
        prev.map(l =>
          l.id === layer.id ? { ...l, selected: false } : l
        )
      );

      return;
    }

    const dataSource = new atlas.source.DataSource(`geopackage-layer-${layer.id}`);
    map.sources.add(dataSource);

    await refreshAssetLayerFeatures(layer, dataSource, map);

    const polygonLayer = new atlas.layer.PolygonLayer(dataSource, undefined, {
      fillColor: 'rgba(23, 125, 220, 0.25)',
      strokeColor: 'rgba(23, 125, 220, 0.8)',
      strokeWidth: 1,
      filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
    });

    const lineLayer = new atlas.layer.LineLayer(dataSource, undefined, {
      strokeColor: 'rgba(23, 125, 220, 0.85)',
      strokeWidth: 2,
      filter: ['any', ['==', ['geometry-type'], 'LineString'], ['==', ['geometry-type'], 'MultiLineString']],
    });

    const pointLayer = new atlas.layer.BubbleLayer(dataSource, undefined, {
      color: 'rgba(23, 125, 220, 0.85)',
      radius: 5,
      strokeColor: 'rgba(255, 255, 255, 0.9)',
      strokeWidth: 1,
      filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],
    });

    const renderedAssetLayers: atlas.layer.Layer[] = [polygonLayer, lineLayer, pointLayer];
    map.layers.add(renderedAssetLayers, 'labels');

    const clickHandlers = renderedAssetLayers.map((mapLayer) => {
      const clickHandler = (e: any) => {
        if (!e?.shapes?.length) {
          return;
        }

        const shape = e.shapes[0];
        let properties: Record<string, unknown> | null = null;

        if (shape instanceof atlas.Shape) {
          properties = shape.getProperties() as Record<string, unknown>;
        }
        else if (shape instanceof atlas.data.Feature) {
          properties = (shape.properties ?? null) as Record<string, unknown> | null;
        }

        if (!properties || Object.keys(properties).length === 0) {
          return;
        }

        if (!popupRef.current) {
          popupRef.current = new atlas.Popup({
            pixelOffset: [0, -20],
            closeButton: true,
          });
        }

        popupRef.current.setOptions({
          content: buildAssetPopupContent(layer.layerDisplayName, properties),
          position: e.position,
        });

        popupRef.current.open(map);
      };

      map.events.add('click', mapLayer, clickHandler);
      return clickHandler;
    });

    // Optional: auto-zoom naar de kaartlaag na renderen.
    // Laat dit voorlopig uitgeschakeld; uncomment om direct te activeren.
    // const firstBbox = (featureCollection?.features ?? []).find(f => Array.isArray(f?.bbox) && f.bbox.length === 4)?.bbox as number[] | undefined;
    // if (firstBbox) {
    //   const zoomBounds = shouldConvertFromRd
    //     ? mapService.convertBboxRDToLatLng(firstBbox, true) as number[]
    //     : firstBbox;
    //
    //   map.setCamera({
    //     bounds: zoomBounds,
    //     padding: 50,
    //     duration: 900,
    //   });
    // }

    const moveHandler = () => { refreshAssetLayerFeatures(layer, dataSource, map); };
    map.events.add('moveend', moveHandler);

    activeAssetLayersRef.current.set(layer.id, {
      dataSource,
      layers: renderedAssetLayers,
      clickHandlers,
      moveHandler,
    });

    setActiveLayers(prev =>
      prev.map(l =>
        l.id === layer.id ? { ...l, selected: true } : l
      )
    );
  };

  if (mapLoadError) {
    return (
      <div className="azure-map-wrapper azure-map-error">
        <div className="melding">
          <span className="material-icons">map</span>
          <span>De kaart kon niet worden geladen. Controleer de internetverbinding of probeer de pagina te vernieuwen.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="azure-map-wrapper">
      <div ref={mapRef} id="mapContainer" className="azure-map" />
      
        <MapSearchControl />

        <MapLayerControl
          layers={activeLayers}
          onToggleLayer={onToggleActiveLayer}
          onToggleGroup={onToggleActiveLayerGroup}
        />

        {currentZoomLevel && currentZoomLevel < 10 && (
          <div className="zoom-level-indicator">
            <div style={{ color: "red", marginTop: "4px" }}>
              Zoom verder in om plannen te tonen.
            </div>
            {/* Zoomniveau: {currentZoomLevel.toFixed(2)}
            {currentZoomLevel < 10 && (
              <div style={{ color: "red", marginTop: "4px" }}>
                Zoom verder in om plannen te tonen.
              </div>
            )} */}
          </div>
        )}
    </div>
  );
};

export default AzureMap;
